import type { AudioCue } from "./audio";

/**
 * The synthesised fallback: one or two oscillator envelopes per cue, for a build without the recorded packs (their
 * manifest failed to load) or a cue the packs lack.
 */
export function playSynth(ctx: AudioContext, out: AudioNode, cue: AudioCue, now: number): void {
  const spec = CUES[cue] ?? CUES.hit;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = spec.type;
  osc.frequency.setValueAtTime(spec.from, now);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, spec.to), now + spec.len);
  gain.gain.setValueAtTime(spec.vol, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + spec.len);
  osc.connect(gain).connect(out);
  osc.start(now);
  osc.stop(now + spec.len + 0.02);
  if (spec.second) {
    const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
    o2.type = spec.second.type; o2.frequency.setValueAtTime(spec.second.from, now + spec.second.delay);
    o2.frequency.exponentialRampToValueAtTime(Math.max(20, spec.second.to), now + spec.second.delay + spec.second.len);
    g2.gain.setValueAtTime(0.0001, now); g2.gain.setValueAtTime(spec.second.vol, now + spec.second.delay);
    g2.gain.exponentialRampToValueAtTime(0.0001, now + spec.second.delay + spec.second.len);
    o2.connect(g2).connect(out); o2.start(now + spec.second.delay); o2.stop(now + spec.second.delay + spec.second.len + 0.02);
  }
}

interface Tone { type: OscillatorType; from: number; to: number; len: number; vol: number }
interface CueSpec extends Tone { second?: Tone & { delay: number } }

const FIRE: CueSpec = { type: "triangle", from: 900, to: 300, len: 0.07, vol: 0.5 };
const HEAVY: CueSpec = { type: "square", from: 160, to: 50, len: 0.18, vol: 0.6 };
const MAGIC: CueSpec = { type: "sine", from: 500, to: 1400, len: 0.12, vol: 0.4 };
const UPGRADE: CueSpec = { type: "sine", from: 500, to: 1000, len: 0.15, vol: 0.5, second: { type: "sine", from: 1000, to: 1500, len: 0.15, vol: 0.4, delay: 0.12 } };
const CUES: Record<AudioCue, CueSpec> = {
  fire_archer: FIRE, fire_ballista: HEAVY, fire_cannon: HEAVY, fire_frost: MAGIC, fire_fire: MAGIC, fire_poison: MAGIC, fire_lightning: MAGIC, fire_beacon: MAGIC,
  deathBig: { type: "sawtooth", from: 120, to: 40, len: 0.4, vol: 0.6 },
  baseCharge: { type: "sawtooth", from: 200, to: 1200, len: 0.8, vol: 0.3 },
  baseStrike: { type: "square", from: 120, to: 40, len: 0.5, vol: 0.7 },
  waveStart: { type: "sawtooth", from: 196, to: 262, len: 0.5, vol: 0.4 },
  waveTick: { type: "sine", from: 880, to: 880, len: 0.12, vol: 0.12 },
  waveMilestone: { type: "triangle", from: 440, to: 660, len: 0.3, vol: 0.35 },
  hit: { type: "square", from: 300, to: 120, len: 0.05, vol: 0.3 },
  death: { type: "sawtooth", from: 220, to: 60, len: 0.16, vol: 0.5 },
  leak: { type: "square", from: 200, to: 80, len: 0.3, vol: 0.7, second: { type: "square", from: 150, to: 60, len: 0.3, vol: 0.6, delay: 0.15 } },
  ui: { type: "sine", from: 660, to: 880, len: 0.05, vol: 0.35 },
  towerPlace: { type: "triangle", from: 300, to: 600, len: 0.1, vol: 0.5 },
  roadPlace: { type: "square", from: 900, to: 500, len: 0.04, vol: 0.3, second: { type: "square", from: 1000, to: 600, len: 0.04, vol: 0.25, delay: 0.075 } },
  towerUpgrade: UPGRADE, towerUpgradeMax: UPGRADE,
  hubUpgradeStart: { type: "triangle", from: 262, to: 392, len: 0.12, vol: 0.45 },
  hubUpgradeDone: UPGRADE,
  purchase: UPGRADE, crateOpen: UPGRADE, achievement: UPGRADE, eventProgress: UPGRADE,
  marketBuy: { type: "sine", from: 660, to: 990, len: 0.1, vol: 0.4 }, marketSell: { type: "sine", from: 990, to: 660, len: 0.1, vol: 0.4 },
  queueSet: { type: "triangle", from: 392, to: 523, len: 0.1, vol: 0.4 },
  chest: { type: "sine", from: 400, to: 800, len: 0.2, vol: 0.5, second: { type: "sine", from: 800, to: 1600, len: 0.3, vol: 0.45, delay: 0.18 } },
  boss: { type: "sawtooth", from: 80, to: 40, len: 0.8, vol: 0.8, second: { type: "square", from: 60, to: 30, len: 0.6, vol: 0.5, delay: 0.3 } },
  bossPhase: { type: "sawtooth", from: 110, to: 45, len: 0.6, vol: 0.7 },
  win: { type: "sine", from: 523, to: 784, len: 0.4, vol: 0.5, second: { type: "sine", from: 784, to: 1046, len: 0.5, vol: 0.5, delay: 0.3 } },
  rareSpawn: { type: "triangle", from: 440, to: 1760, len: 0.9, vol: 0.5, second: { type: "sine", from: 880, to: 1320, len: 0.6, vol: 0.45, delay: 0.85 } },
  rareKill: { type: "sine", from: 659, to: 880, len: 0.4, vol: 0.5, second: { type: "sine", from: 880, to: 1320, len: 0.6, vol: 0.5, delay: 0.3 } },
  lose: { type: "sawtooth", from: 300, to: 80, len: 0.9, vol: 0.5 },
  fire_mortar: { type: "square", from: 120, to: 40, len: 0.25, vol: 0.6 },
  mortarWhistle: { type: "sine", from: 1800, to: 600, len: 0.8, vol: 0.2 },
  mortarImpact: { type: "sawtooth", from: 110, to: 30, len: 0.5, vol: 0.7 },
  barracksDeploy: { type: "triangle", from: 220, to: 160, len: 0.12, vol: 0.45, second: { type: "triangle", from: 180, to: 140, len: 0.08, vol: 0.4, delay: 0.25 } },
  fire_arcane: { type: "sine", from: 700, to: 2400, len: 0.18, vol: 0.35 },
  hexCurse: { type: "sine", from: 300, to: 140, len: 0.6, vol: 0.35, second: { type: "sine", from: 318, to: 150, len: 0.6, vol: 0.25, delay: 0.02 } },
  chestLand: HEAVY, chestRattle: { type: "square", from: 260, to: 180, len: 0.06, vol: 0.35 }, chestTierUp: UPGRADE,
  chestLockBreak: { type: "triangle", from: 1400, to: 700, len: 0.08, vol: 0.4 },
  chestBurst: { type: "sine", from: 400, to: 800, len: 0.2, vol: 0.5, second: { type: "sine", from: 800, to: 1600, len: 0.3, vol: 0.45, delay: 0.18 } },
  chestCoins: { type: "triangle", from: 2000, to: 1500, len: 0.25, vol: 0.2 }, cardFlip: { type: "triangle", from: 900, to: 500, len: 0.05, vol: 0.25 },
  cardRare: MAGIC, rarityEpic: UPGRADE, rarityLegendary: UPGRADE, rarityMythic: UPGRADE,
  chestCollect: { type: "sine", from: 1800, to: 2200, len: 0.04, vol: 0.2 }, chestWhoosh: { type: "sawtooth", from: 200, to: 600, len: 0.3, vol: 0.15 },
  mythicArc: { type: "sawtooth", from: 90, to: 140, len: 0.5, vol: 0.2 },
  wyrm_herald: { type: "sine", from: 110, to: 440, len: 1.0, vol: 0.3 },
  wyrm_dive: { type: "sine", from: 2400, to: 400, len: 0.9, vol: 0.2, second: { type: "square", from: 120, to: 35, len: 0.5, vol: 0.7, delay: 0.95 } },
  wyrm_roar: { type: "sawtooth", from: 140, to: 60, len: 0.9, vol: 0.6 }, wyrm_return: { type: "sawtooth", from: 150, to: 60, len: 0.9, vol: 0.6 },
  wyrm_wingbeat: { type: "triangle", from: 120, to: 60, len: 0.15, vol: 0.2 }, wyrm_shimmer: { type: "sine", from: 2637, to: 3520, len: 0.2, vol: 0.08 },
  wyrm_hit: { type: "sine", from: 3520, to: 3300, len: 0.08, vol: 0.2 },
  wyrm_death: { type: "sawtooth", from: 400, to: 90, len: 1.2, vol: 0.5 }, wyrm_crash: HEAVY,
  wyrm_flee: { type: "sawtooth", from: 200, to: 900, len: 0.8, vol: 0.3 }, wyrm_choice: { type: "sine", from: 220, to: 233, len: 0.8, vol: 0.3 },
  wyrm_unlock: { type: "sine", from: 440, to: 880, len: 0.3, vol: 0.5, second: { type: "sine", from: 880, to: 1760, len: 0.5, vol: 0.45, delay: 0.25 } },
};

/**
 * The last stand's heartbeat (no recorded pack: it is synthesised on the fly). A "lub" and a softer, higher "dub" a
 * fifth of a second later: each a sine that drops an octave in pitch under a fast-attack, short-decay envelope, low
 * enough to be felt more than heard, and kept under 100 Hz's harmonics so it never masks a leak or a death.
 */
const HEART = {
  lub: { from: 62, to: 38, len: 0.16, vol: 1 },
  dub: { from: 74, to: 44, len: 0.13, vol: 0.7, delay: 0.19 },
  attack: 0.008,
} as const;

export function playHeartbeat(ctx: AudioContext, out: AudioNode, at: number, level: number): void {
  for (const beat of [HEART.lub, HEART.dub]) {
    const t = at + ("delay" in beat ? beat.delay : 0);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(beat.from, t);
    osc.frequency.exponentialRampToValueAtTime(beat.to, t + beat.len);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, beat.vol * level), t + HEART.attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + beat.len);
    osc.connect(g).connect(out);
    osc.start(t);
    osc.stop(t + beat.len + 0.02);
  }
}
